import axios from 'axios';
import { toast } from 'sonner';

// Auto-detect backend URL from current origin (works in both preview and production).
// Sin fallback a env: en runtime SIEMPRE existe window. La env var del build de CRA
// puede tener valores stale del preview que romperían producción tras un dominio nuevo.
const BACKEND_URL = typeof window !== 'undefined' ? window.location.origin : '';
const API_URL = `${BACKEND_URL}/api`;

// Token fallback storage (used ONLY when httpOnly cookies fail in certain browsers)
const TOKEN_KEY = '_auth_fb';
export const setFallbackToken = (token) => {
  try { sessionStorage.setItem(TOKEN_KEY, token); } catch { /* ignore */ }
};
export const getFallbackToken = () => {
  try { return sessionStorage.getItem(TOKEN_KEY); } catch { return null; }
};
export const clearFallbackToken = () => {
  try { sessionStorage.removeItem(TOKEN_KEY); } catch { /* ignore */ }
};

// Aviso de una sola lectura para la pantalla de login (p.ej. "tu sesión expiró")
const NOTICE_KEY = '_login_notice';
export const setLoginNotice = (msg) => {
  try { sessionStorage.setItem(NOTICE_KEY, msg); } catch { /* ignore */ }
};
export const consumeLoginNotice = () => {
  try {
    const m = sessionStorage.getItem(NOTICE_KEY);
    if (m) sessionStorage.removeItem(NOTICE_KEY);
    return m || null;
  } catch { return null; }
};

// Explica en español POR QUÉ falló una llamada (credenciales, rate limit, timeout, sin red, 5xx, proxy caído)
export const describirErrorServidor = (error) => {
  const res = error?.response;
  if (!res) {
    if (error?.code === 'ECONNABORTED' || /timeout/i.test(error?.message || '')) {
      return 'El servidor tardó demasiado en responder (timeout). Puede estar sobrecargado; reintenta en unos segundos.';
    }
    return 'No hay conexión con el servidor. Revisa tu internet o que el servicio esté activo.';
  }
  const detail = typeof res.data?.detail === 'string' ? res.data.detail : null;
  const s = res.status;
  if (s === 401) return detail === 'Usuario desactivado' ? 'Tu usuario está desactivado. Contacta a un administrador.' : 'Correo o contraseña incorrectos.';
  if (s === 429) return detail || 'Demasiados intentos. Espera un momento antes de reintentar.';
  if (s === 503 || s === 502 || s === 504) return `El servidor no está disponible en este momento (HTTP ${s}). Si el entorno estaba inactivo, espera unos segundos y reintenta.`;
  if (s >= 500) return `Error interno del servidor (HTTP ${s}). ${detail || 'Reintenta; si persiste, avisa a soporte.'}`;
  return detail || `Error HTTP ${s}`;
};

const api = axios.create({
  baseURL: API_URL,
  timeout: 120000,
  withCredentials: true,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Attach Bearer token as fallback when cookies might not be sent
api.interceptors.request.use((config) => {
  const token = getFallbackToken();
  if (token && !config.headers.Authorization) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Handle 401 errors (skip for login/register/me to avoid redirect loop)
const manejarErrorRespuesta = (error) => {
  const url = error.config?.url || '';
  const isAuthRoute = url.includes('/auth/login') || url.includes('/auth/register') || url.includes('/auth/me');
  if (error.response?.status === 401 && !isAuthRoute) {
    clearFallbackToken();
    setLoginNotice('Tu sesión expiró o fue cerrada en otro dispositivo. Inicia sesión nuevamente.');
    window.location.href = '/login';
    return Promise.reject(error);
  }
  // Servidor caído / sin red / 5xx: avisar SIEMPRE por qué no responde (un solo toast, se actualiza en vez de apilarse)
  const status = error.response?.status;
  if (!isAuthRoute && (!error.response || status >= 500)) {
    const method = (error.config?.method || 'get').toUpperCase();
    toast.error('El servidor no responde', {
      id: 'servidor-no-responde',
      description: `${describirErrorServidor(error)} (${method} ${url})`,
      duration: 8000,
    });
  }
  return Promise.reject(error);
};

api.interceptors.response.use(
  (response) => {
    // Respuesta HTML en un endpoint /api (proxy del preview despertando, página de error del edge): tratar como fallo
    if (typeof response.data === 'string' && /^\s*<(!doctype|html)/i.test(response.data)) {
      const err = Object.assign(new Error('Respuesta no válida del servidor (HTML)'), {
        config: response.config,
        response: { ...response, status: 503, data: { detail: 'Servidor no disponible, reintenta en unos segundos' } },
      });
      return manejarErrorRespuesta(err);
    }
    return response;
  },
  manejarErrorRespuesta
);

// Auth
export const authApi = {
  login: (data) => api.post('/auth/login', data),
  register: (data) => api.post('/auth/register', data),
  logout: () => api.post('/auth/logout'),
  me: () => api.get('/auth/me'),
};

// Clientes
export const clientesApi = {
  getAll: () => api.get('/clientes'),
  getOne: (id) => api.get(`/clientes/${id}`),
  create: (data) => api.post('/clientes', data),
  update: (id, data) => api.put(`/clientes/${id}`, data),
  delete: (id) => api.delete(`/clientes/${id}`),
  // Iter 292.b (Carlos, feb-2026) — Importar clientes desde Alegra (tipo=client).
  importarAlegra: (body = { dry_run: false }) => api.post('/clientes/importar-alegra', body),
  // Iter 415 — Backfill del maestro desde los DTE emitidos por CHIRINO (33/34).
  backfillDte: (body = { dry_run: true, modo: 'solo_vacios' }) => api.post('/clientes/backfill-dte', body),
};

// Iter 415 — Módulo de Emisión de DTE + Cesión/Factoring
export const emisionDteApi = {
  list: (params) => api.get('/emision-dte', { params }),
  get: (id) => api.get(`/emision-dte/${id}`),
  crear: (data) => api.post('/emision-dte', data),
  editar: (id, data) => api.put(`/emision-dte/${id}`, data),
  validar: (id) => api.get(`/emision-dte/${id}/validar`),
  preview: (id) => api.get(`/emision-dte/${id}/preview`),
  duplicados: (id) => api.get(`/emision-dte/${id}/duplicados`),
  emitir: (id, body) => api.post(`/emision-dte/${id}/emitir`, body),
  reenviar: (id, body) => api.post(`/emision-dte/${id}/reenviar`, body || {}),
  limpiar: (ids) => api.post('/emision-dte/limpiar', { ids }),
  restaurar: (id) => api.post(`/emision-dte/${id}/restaurar`),
  contabilizar: (id) => api.post(`/emision-dte/${id}/contabilizar`),
  reintentarContab: (id) => api.post(`/emision-dte/${id}/reintentar-contabilizacion`),
  factoring: (id, body) => api.post(`/emision-dte/${id}/factoring`, body),
  eventoReceptor: (id, body) => api.post(`/emision-dte/${id}/eventos-receptor`, body),
  folios: () => api.get('/emision-dte/folios'),
  setFolios: (tipo, body) => api.put(`/emision-dte/folios/${tipo}`, body),
  getConfig: () => api.get('/emision-dte/config'),
  setConfig: (body) => api.put('/emision-dte/config', body),
  probarConexion: () => api.post('/emision-dte/config/probar'),
  documentoUrl: (id, fmt) => `${API_URL}/emision-dte/${id}/documento/${fmt}`,
  documento: (id, fmt) => api.get(`/emision-dte/${id}/documento/${fmt}`, { responseType: 'blob' }),
  representacion: (id) => api.get(`/emision-dte/${id}/representacion`, { responseType: 'blob' }),
  subirAdjunto: (id, formData) => api.post(`/emision-dte/${id}/adjuntos`, formData, { headers: { 'Content-Type': 'multipart/form-data' } }),
  descargarAdjunto: (id, adjId) => api.get(`/emision-dte/${id}/adjuntos/${adjId}/descargar`, { responseType: 'blob' }),
  eliminarAdjunto: (id, adjId) => api.delete(`/emision-dte/${id}/adjuntos/${adjId}`),
  traerExterno: (body) => api.post('/emision-dte/externo/traer', body),
  syncExternos: () => api.post('/emision-dte/externo/sync'),
  asociarProforma: (id, proforma_id) => api.post(`/emision-dte/${id}/asociar-proforma`, { proforma_id }),
  anular: (id, body) => api.post(`/emision-dte/${id}/anular`, body || {}),
  reversarNcRechazada: (body) => api.post('/emision-dte/admin/reversar-nc-rechazada', body || {}),
  recontabilizar: (body) => api.post('/emision-dte/admin/recontabilizar', body || {}),
  verificarSii: (id) => api.post(`/emision-dte/${id}/verificar-sii`),
  listarProformas: (params) => api.get('/facturacion/proformas', { params }),
};

// Proyectos
export const proyectosApi = {
  getAll: (params) => api.get('/proyectos', { params }),
  getOne: (id) => api.get(`/proyectos/${id}`),
  create: (data) => api.post('/proyectos', data),
  update: (id, data) => api.put(`/proyectos/${id}`, data),
  delete: (id) => api.delete(`/proyectos/${id}`),
};

// Ordenes de Trabajo
export const ordenesApi = {
  getAll: (params) => api.get('/ordenes-trabajo', { params }),
  getOne: (id) => api.get(`/ordenes-trabajo/${id}`),
  create: (data) => api.post('/ordenes-trabajo', data),
  update: (id, data) => api.put(`/ordenes-trabajo/${id}`, data),
  delete: (id) => api.delete(`/ordenes-trabajo/${id}`),
  patch: (id, data) => api.patch(`/ordenes-trabajo/${id}`, data),
  numbering: (proyectoCodigo) => api.get('/ot-numbering', { params: { proyecto_codigo: proyectoCodigo } }),
};

// Subproyectos
export const subproyectosApi = {
  getByProyecto: (proyectoCodigo) => api.get(`/subproyectos/${proyectoCodigo}`),
};

// Areas
export const areasApi = {
  getAll: () => api.get('/areas'),
  create: (data) => api.post('/areas', data),
  delete: (id) => api.delete(`/areas/${id}`),
};

// Dashboard
export const dashboardApi = {
  getStats: () => api.get('/dashboard/stats'),
  getRecentOts: (limit = 5) => api.get('/dashboard/recent-ots', { params: { limit } }),
};

// Access Database
export const accessApi = {
  upload: async (file, onProgress) => {
    const CHUNK_SIZE = 4 * 1024 * 1024;
    const totalChunks = Math.ceil(file.size / CHUNK_SIZE);
    if (totalChunks <= 1) {
      const formData = new FormData();
      formData.append('file', file);
      return api.post('/access/upload', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
        timeout: 300000,
        onUploadProgress: onProgress,
      });
    }
    let lastResult = null;
    for (let i = 0; i < totalChunks; i++) {
      const start = i * CHUNK_SIZE;
      const end = Math.min(start + CHUNK_SIZE, file.size);
      const chunk = file.slice(start, end);
      const formData = new FormData();
      formData.append('file', new File([chunk], `chunk_${i}`, { type: file.type }));
      lastResult = await api.post(
        `/access/upload-chunk?chunk_index=${i}&total_chunks=${totalChunks}&filename=${encodeURIComponent(file.name)}`,
        formData,
        { headers: { 'Content-Type': 'multipart/form-data' }, timeout: 120000 }
      );
      if (onProgress) onProgress({ loaded: end, total: file.size });
    }
    return lastResult;
  },
  getInfo: () => api.get('/access/info'),
  getTables: () => api.get('/access/tables'),
  getTableData: (tableName, limit = 100) => api.get(`/access/table/${tableName}`, { params: { limit } }),
  deleteFile: () => api.delete('/access/file'),
  importData: () => api.post('/access/import', {}, { timeout: 30000 }),
  importHH: () => api.post('/access/import-hh', {}, { timeout: 30000 }),
  importVentas: () => api.post('/access/import-ventas', {}, { timeout: 30000 }),
  importActivoCirculante: () => api.post('/access/import-activo-circulante', {}, { timeout: 30000 }),
  updateAvances: () => api.post('/access/update-avances', {}, { timeout: 120000 }),
  repairTools: () => api.post('/access/repair-tools', {}, { timeout: 120000 }),
  importCompras: () => api.post('/access/import-compras', {}, { timeout: 30000 }),
  importMateriales: () => api.post('/access/import-materiales', {}, { timeout: 30000 }),
  importTodo: () => api.post('/access/import-todo', {}, { timeout: 30000 }),
  getImportStatus: () => api.get('/access/import-status'),
  resetImportStatus: () => api.post('/access/reset-import-status', {}, { timeout: 10000 }),
};

// Flujo de Caja
export const flujoCajaApi = {
  getResumen: () => api.get('/flujo-caja/resumen'),
  getVencimientos: (tipo = '') => api.get(`/flujo-caja/vencimientos?tipo=${tipo}`),
  getGrid: (opts = {}) => api.get('/flujo-caja/grid', { timeout: 60000, params: opts.nocache ? { nocache: true } : {} }),
  // Provisiones CRUD
  listProvisiones: (activo) => api.get(`/flujo-caja/provisiones${activo !== undefined ? `?activo=${activo}` : ''}`),
  createProvision: (data) => api.post('/flujo-caja/provisiones', data),
  updateProvision: (id, data) => api.put(`/flujo-caja/provisiones/${id}`, data),
  deleteProvision: (id) => api.delete(`/flujo-caja/provisiones/${id}`),
  generarMesesProvision: (id, meses) => api.post(`/flujo-caja/provisiones/${id}/generar-meses`, { meses }),
  // Proyección 12m CRUD
  listProyeccion: () => api.get('/flujo-caja/proyeccion-12m'),
  createProyeccion: (data) => api.post('/flujo-caja/proyeccion-12m', data),
  updateProyeccion: (id, data) => api.put(`/flujo-caja/proyeccion-12m/${id}`, data),
  deleteProyeccion: (id) => api.delete(`/flujo-caja/proyeccion-12m/${id}`),
  toggleMesPagado: (id, mesIdx) => api.patch(`/flujo-caja/proyeccion-12m/${id}/toggle-mes/${mesIdx}`),
  setVencidoManual: (id, monto) => api.patch(`/flujo-caja/proyeccion-12m/${id}/vencido-manual`, { monto }),
  marcarVencidoPagado: (id) => api.post(`/flujo-caja/proyeccion-12m/${id}/vencido/marcar-pagado`),
  reprogramarVencido: (id, targetYm) => api.post(`/flujo-caja/proyeccion-12m/${id}/vencido/reprogramar`, { target_ym: targetYm }),
  // Bancos CRUD
  listBancos: () => api.get('/flujo-caja/bancos'),
  createBanco: (data) => api.post('/flujo-caja/bancos', data),
  updateBanco: (id, data) => api.put(`/flujo-caja/bancos/${id}`, data),
  deleteBanco: (id) => api.delete(`/flujo-caja/bancos/${id}`),
  // Grid shifts (desplazar montos entre días)
  listGridShifts: () => api.get('/flujo-caja/grid-shifts'),
  createGridShift: (data) => api.post('/flujo-caja/grid-shifts', data),
  deleteGridShift: (id) => api.delete(`/flujo-caja/grid-shifts/${id}`),
  // CxC (Clientes por Cobrar)
  listCxC: (estado) => api.get(`/flujo-caja/cxc${estado ? `?estado=${estado}` : ''}`),
  createCxC: (data) => api.post('/flujo-caja/cxc', data),
  updateCxC: (id, data) => api.put(`/flujo-caja/cxc/${id}`, data),
  cambiarFechaCxC: (id, fecha) => api.patch(`/flujo-caja/cxc/${id}/fecha-venc?fecha=${fecha}`),
  deleteCxC: (id) => api.delete(`/flujo-caja/cxc/${id}`),
  importCxCAlegra: () => api.post('/flujo-caja/cxc/importar-alegra', {}, { timeout: 120000 }),

  // Factoring
  listFactoringLineas: (incluirInactivas = false) => api.get(`/flujo-caja/factoring/lineas${incluirInactivas ? '?incluir_inactivas=true' : ''}`),
  createFactoringLinea: (data) => api.post('/flujo-caja/factoring/lineas', data),
  updateFactoringLinea: (id, data) => api.put(`/flujo-caja/factoring/lineas/${id}`, data),
  deleteFactoringLinea: (id) => api.delete(`/flujo-caja/factoring/lineas/${id}`),
  listFactoringAsignaciones: (params = {}) => {
    const qs = new URLSearchParams();
    if (params.linea_id) qs.set('linea_id', params.linea_id);
    if (params.estado) qs.set('estado', params.estado);
    if (params.solo_vencidas) qs.set('solo_vencidas', 'true');
    const q = qs.toString();
    return api.get(`/flujo-caja/factoring/asignaciones${q ? `?${q}` : ''}`);
  },
  createFactoringAsignacion: (data) => api.post('/flujo-caja/factoring/asignaciones', data),
  marcarPagadaAsignacion: (id) => api.patch(`/flujo-caja/factoring/asignaciones/${id}/marcar-pagada`),
  marcarRecuperadaAsignacion: (id) => api.patch(`/flujo-caja/factoring/asignaciones/${id}/marcar-recuperada`),
  factoringDiscrepancias: () => api.get('/flujo-caja/factoring/discrepancias'),
  reintentarAlegraAsignacion: (id) => api.patch(`/flujo-caja/factoring/asignaciones/${id}/reintentar-alegra`),
  liberarAsignacion: (id) => api.patch(`/flujo-caja/factoring/asignaciones/${id}/liberar`),
  setFechaPagoFactoring: (id, fecha) =>
    api.patch(`/flujo-caja/factoring/asignaciones/${id}/fecha-pago`, { fecha: fecha || null }),
  deleteFactoringAsignacion: (id) => api.delete(`/flujo-caja/factoring/asignaciones/${id}`),
  cxcDisponiblesFactoring: () => api.get('/flujo-caja/factoring/cxc-disponibles'),
  asignacionRapida: (linea_id, cxc_id) => api.post('/flujo-caja/factoring/asignaciones/rapida', { linea_id, cxc_id }),

  // Otros Ingresos (Iter 236.o) — CRUD + factoring
  listOtrosIngresos: (estado) => api.get(`/flujo-caja/otros-ingresos${estado ? `?estado=${estado}` : ''}`),
  createOtroIngreso: (data) => api.post('/flujo-caja/otros-ingresos', data),
  updateOtroIngreso: (id, data) => api.put(`/flujo-caja/otros-ingresos/${id}`, data),
  cambiarFechaOtroIngreso: (id, fecha) => api.patch(`/flujo-caja/otros-ingresos/${id}/fecha-venc?fecha=${fecha}`),
  deleteOtroIngreso: (id) => api.delete(`/flujo-caja/otros-ingresos/${id}`),
  marcarPagadaOtroIngreso: (id) => api.patch(`/flujo-caja/otros-ingresos/${id}/marcar-pagada`),
  otrosIngresosDisponiblesFactoring: () => api.get('/flujo-caja/factoring/otros-ingresos-disponibles'),
  asignacionRapidaOtroIngreso: (linea_id, otros_ingreso_id) =>
    api.post('/flujo-caja/factoring/asignaciones-otros-ingresos/rapida', { linea_id, otros_ingreso_id }),
  // CxC marcar pagada + reintento
  marcarPagadaCxC: (id) => api.patch(`/flujo-caja/cxc/${id}/marcar-pagada`),
  reintentarAlegraCxC: (id) => api.patch(`/flujo-caja/cxc/${id}/reintentar-alegra`),
  // CxP reintento a Alegra (pagos-out)
  listFacturasCxPConErrorAlegra: () => api.get('/flujo-caja/cxp/facturas/con-error-alegra'),
  reintentarAlegraCxP: (id) => api.patch(`/flujo-caja/cxp/facturas/${id}/reintentar-alegra`),
  // CxP detector de facturas duplicadas en Alegra (Iter 237)
  detectarDuplicadosAlegra: (verificar = true) =>
    api.get(`/flujo-caja/cxp/duplicados-alegra?verificar=${verificar}`),
  // Gastos Diarios Imprevistos (config)
  getGastosDiariosConfig: () => api.get('/flujo-caja/config/gastos-diarios'),
  updateGastosDiariosConfig: (data) => api.put('/flujo-caja/config/gastos-diarios', data),
};

// Ventas/Facturacion
export const ventasApi = {
  getPorOT: (otNumero) => api.get(`/ventas/por-ot/${otNumero}`),
  getDetalle: (idVenta) => api.get(`/ventas/detalle/${idVenta}`),
  getResumenPorProyecto: () => api.get('/ventas/resumen-por-proyecto'),
  getSinOT: (params) => api.get('/ventas/sin-ot', { params }),
  asignarOT: (idVenta, otNumero) => api.patch(`/ventas/${encodeURIComponent(idVenta)}/asignar-ot`, { ot_numero: otNumero }),
};

// Operaciones
export const operacionesApi = {
  getActivoCirculante: () => api.get('/operaciones/activo-circulante'),
  updateOTCirculante: (numeroOt, data) => api.patch(`/operaciones/activo-circulante/ot/${numeroOt}`, data),
  getActivoCirculantePDF: () => api.get('/operaciones/activo-circulante/pdf', { responseType: 'blob', timeout: 60000 }),
  getConfigEnvioAC: () => api.get('/operaciones/activo-circulante/config-envio'),
  saveConfigEnvioAC: (data) => api.put('/operaciones/activo-circulante/config-envio', data),
  enviarInformeAC: () => api.post('/operaciones/activo-circulante/enviar-informe', {}, { timeout: 90000 }),
};

// Compras
export const comprasApi = {
  getResumen: () => api.get('/compras/resumen'),
  getOrdenesCompra: (params) => api.get('/compras/ordenes-compra', { params }),
  getOrdenCompraDetalle: (numeroOc) => api.get(`/compras/ordenes-compra/${numeroOc}`),
  getPedidos: (params) => api.get('/compras/pedidos', { params }),
  getPedidoDetalle: (numeroPedido) => api.get(`/compras/pedidos/${numeroPedido}`),
  getRecepciones: (params) => api.get('/compras/recepciones', { params }),
  getRecepcionDetalle: (numeroRecepcion) => api.get(`/compras/recepciones/${numeroRecepcion}`),
  getCostoMaterialesOT: (otNumero) => api.get('/compras/costo-materiales-por-ot', { params: { ot_numero: otNumero } }),
  getResumenOTsEjecucion: () => api.get('/compras/resumen-ots-ejecucion'),
  getEstadoMaterialesOT: (otNumero) => api.get(`/compras/estado-materiales/${otNumero}`),
  getEstadoCompletoPorOT: (otNumero) => api.get(`/compras/estado-completo-ot/${otNumero}`),
  getOCPendientes: (source = 'web') => api.get('/compras/oc-pendientes', { params: { source } }),
  getPedidosSinGestionar: () => api.get('/compras/pedidos-sin-gestionar'),
};

// Partes VB Supervisor
export const partesVbApi = {
  pinStatus: () => api.get('/partes-vb/me/pin-status'),
  setPin: (pin) => api.put('/partes-vb/me/pin', { pin }),
  deletePin: () => api.delete('/partes-vb/me/pin'),
  misPendientes: () => api.get('/partes-vb/mis-pendientes'),
  misHistorial: (limit = 50) => api.get(`/partes-vb/mis-historial?limit=${limit}`),
  darVB: (parteId, pin, comentario = '') => api.post(`/partes-vb/${parteId}/dar-vb`, { pin, comentario }),
  status: (parteId) => api.get(`/partes-vb/${parteId}/status`),
  detalle: (parteId) => api.get(`/partes-vb/${parteId}/detalle`),
  // Admin: partes huérfanos
  adminHuerfanos: () => api.get('/partes-vb/admin/huerfanos'),
  adminSupervisoresActivos: () => api.get('/partes-vb/admin/supervisores-activos'),
  adminReasignarSupervisor: (parte_id, nuevo_supervisor_user_id, motivo = '') =>
    api.post('/partes-vb/admin/reasignar-supervisor', { parte_id, nuevo_supervisor_user_id, motivo }),
};

// Proveedores CRUD
export const proveedoresApi = {
  list: (params) => api.get('/proveedores', { params }),
  get: (id) => api.get(`/proveedores/${id}`),
  create: (data) => api.post('/proveedores', data),
  update: (id, data) => api.put(`/proveedores/${id}`, data),
  toggleActivo: (id) => api.patch(`/proveedores/${id}/toggle-activo`),
  remove: (id) => api.delete(`/proveedores/${id}`),
  importarAlegra: (body = { dry_run: false, solo_con_email: false }) =>
    api.post('/proveedores/importar-alegra', body, { timeout: 180000 }),
};

// Wiki / Manual de Uso
export const wikiApi = {
  listCategorias: () => api.get('/wiki/categorias'),
  list: (params) => api.get('/wiki', { params }),
  get: (slug) => api.get(`/wiki/${slug}`),
  create: (data) => api.post('/wiki', data),
  update: (id, data) => api.put(`/wiki/${id}`, data),
  remove: (id) => api.delete(`/wiki/${id}`),
};

// Trabajadores
export const trabajadoresApi = {
  getAll: (params) => api.get('/trabajadores', { params }),
  getOne: (numero) => api.get(`/trabajadores/${numero}`),
  create: (data) => api.post('/trabajadores', data),
  update: (numero, data) => api.put(`/trabajadores/${numero}`, data),
  getAreas: () => api.get('/trabajadores/areas'),
  getCargos: () => api.get('/trabajadores/cargos'),
};

// Vacaciones y Feriado Legal (Chile)
export const vacacionesApi = {
  miSaldo: () => api.get('/vacaciones/mi-saldo'),
  saldos: () => api.get('/vacaciones/saldos'),
  saldoTrabajador: (numero) => api.get(`/vacaciones/saldo/${numero}`),
  listSolicitudes: (params) => api.get('/vacaciones/solicitudes', { params }),
  crearSolicitud: (data) => api.post('/vacaciones/solicitudes', data),
  aprobarN1: (id, data) => api.post(`/vacaciones/solicitudes/${id}/aprobar-n1`, data || {}),
  aprobarN2: (id, data) => api.post(`/vacaciones/solicitudes/${id}/aprobar-n2`, data || {}),
  rechazar: (id, data) => api.post(`/vacaciones/solicitudes/${id}/rechazar`, data || {}),
  cancelar: (id) => api.post(`/vacaciones/solicitudes/${id}/cancelar`),
  pendientes: () => api.get('/vacaciones/pendientes-aprobacion'),
  resumenDashboard: () => api.get('/vacaciones/resumen-dashboard'),
  planner: (params) => api.get('/vacaciones/planner', { params }),
  calcularDias: (params) => api.get('/vacaciones/calcular-dias', { params }),
  comprobanteUrl: (id) => `${API_URL}/vacaciones/solicitudes/${id}/comprobante`,
  comprobante: (id) => api.get(`/vacaciones/solicitudes/${id}/comprobante`, { responseType: 'blob' }),
};

// Control HH
export const controlHHApi = {
  getAll: (params) => api.get('/control-hh', { params }),
  consulta: (params) => api.get('/control-hh/consulta', { params }),
  carga: (data) => api.post('/control-hh/carga', data),
  delete: (id) => api.delete(`/control-hh/${id}`),
  getResumenOT: (otNumero) => api.get(`/control-hh/resumen-ot/${otNumero}`),
  getDetalleOT: (otNumero) => api.get(`/control-hh/detalle-ot/${otNumero}`),
  resumenMensual: (params) => api.get('/control-hh/resumen-mensual', { params }),
  resumenGeneral: (params) => api.get('/control-hh/resumen-general', { params }),
  detallePersona: (params) => api.get('/control-hh/detalle-persona', { params }),
};

// RRHH
export const rrhhApi = {
  getDashboard: (params) => api.get('/rrhh/dashboard', { params }),
  getOTsActivas: () => api.get('/rrhh/ots-activas'),
  abrirParte: (data) => api.post('/rrhh/parte-diario/abrir', data),
  getParte: (id) => api.get(`/rrhh/parte-diario/${id}`),
  listPartes: (params) => api.get('/rrhh/partes-diarios', { params }),
  updateParte: (id, data) => api.put(`/rrhh/parte-diario/${id}`, data),
  cerrarParte: (id) => api.post(`/rrhh/parte-diario/${id}/cerrar`),
  reabrirParte: (id) => api.post(`/rrhh/parte-diario/${id}/reabrir`),
  getParteConfig: () => api.get('/rrhh/config/parte-diario'),
  updateParteConfig: (data) => api.put('/rrhh/config/parte-diario', data),
  enviarAlmuerzo: (data) => api.post('/rrhh/parte-diario/enviar-almuerzo', data),
  getResumenAlmuerzos: (params) => api.get('/rrhh/resumen-almuerzos', { params }),
  enviarResumenConsolidado: (data) => api.post('/rrhh/resumen-almuerzos/enviar-pedido', data),
  getConcesionarios: (params) => api.get('/rrhh/concesionarios', { params }),
  upsertConcesionario: (data) => api.put('/rrhh/concesionario', data),
  deleteConcesionario: (lugar) => api.delete(`/rrhh/concesionario/${encodeURIComponent(lugar)}`),
  reactivarConcesionario: (lugar) => api.post(`/rrhh/concesionario/${encodeURIComponent(lugar)}/reactivar`),
  // Iter 236.v — búsqueda proveedores Alegra para vincular a concesionario
  searchProveedoresConcesionario: (q) =>
    api.get('/rrhh/concesionarios/proveedores-search', { params: { q } }),
  patchAlmuerzoTrabajador: (data) => api.patch('/rrhh/resumen-almuerzos/trabajador', data),
  agregarTrabajadorAlmuerzo: (data) => api.post('/rrhh/resumen-almuerzos/agregar', data),
  eliminarParte: (id) => api.post(`/rrhh/parte-diario/${id}/eliminar`),
  clasificarAusencia: (data) => api.post('/rrhh/panel-personal/clasificar-ausencia', data),
  getResumenCasino: (params) => api.get('/rrhh/resumen-casino', { params }),
  getPersonalAnterior: (parteId) => api.get(`/rrhh/parte-diario/${parteId}/personal-anterior`),
  getPanelPersonal: (params) => api.get('/rrhh/panel-personal', { params }),
  checkAlmuerzo: (params) => api.get('/rrhh/almuerzo-check', { params }),
  getAlmuerzoConflictos: (parteId) => api.get(`/rrhh/parte-diario/${parteId}/almuerzo-conflictos`),
};

// Tarjeta Control
export const tarjetaControlApi = {
  get: (params) => api.get('/rrhh/tarjeta-control', { params }),
  save: (data) => api.put('/rrhh/tarjeta-control', data),
  aplicarHorario: (params) => api.post('/rrhh/tarjeta-control/aplicar-horario', null, { params }),
  list: (params) => api.get('/rrhh/tarjetas-control-lista', { params }),
  getErrores: (params) => api.get('/rrhh/control-errores', { params }),
  saveNota: (data) => api.put('/rrhh/control-errores/nota', data),
  getAsistenciaMensual: (params) => api.get('/rrhh/asistencia-mensual', { params }),
  clasificarDia: (data) => api.put('/rrhh/asistencia-mensual/clasificar', data),
  toggleFeriado: (data) => api.put('/rrhh/asistencia-mensual/feriado', data),
  autoMarcarFeriados: (mes) => api.post('/rrhh/asistencia-mensual/feriados/auto-marcar', { mes }),
  exportAsistenciaExcel: (mes) => api.get('/rrhh/asistencia-mensual/export-excel', { params: { mes }, responseType: 'blob' }),
};

// Reportes
export const reportesApi = {
  getHHPorOT: (params) => api.get('/reportes/hh-por-ot', { params }),
  getHHPorProyecto: (params) => api.get('/reportes/hh-por-proyecto', { params }),
  getProyectosLista: () => api.get('/reportes/proyectos-lista'),
  getOTsLista: () => api.get('/reportes/ots-lista'),
};

// Monitoring
export const monitoringApi = {
  getHealth: () => api.get('/monitoring/health'),
  getDbStats: () => api.get('/monitoring/db-stats'),
  getActivity: (limit = 30) => api.get('/monitoring/activity', { params: { limit } }),
  getModuleStats: () => api.get('/monitoring/module-stats'),
  getAlerts: () => api.get('/monitoring/alerts'),
  getAlertConfig: () => api.get('/monitoring/alert-config'),
  updateAlertConfig: (config) => api.put('/monitoring/alert-config', config),
  dismissAlert: (alertId, hours = 24) => api.post(`/monitoring/alerts/${alertId}/dismiss`, null, { params: { hours } }),
};

// Notifications
export const notificationsApi = {
  getTypes: () => api.get('/notifications/types'),
  getConfig: () => api.get('/notifications/config'),
  updateConfig: (data) => api.put('/notifications/config', data),
  updateConfigBulk: (configs) => api.put('/notifications/config/bulk', { configs }),
  check: () => api.get('/notifications/check'),
  sendEmail: () => api.post('/notifications/send-email'),
  testEmail: (email) => api.post('/notifications/test-email', { email }),
  getHistory: (limit = 50) => api.get('/notifications/history', { params: { limit } }),
  clearHistory: () => api.delete('/notifications/history'),
};

// Conciliación de Estados de Cuenta de Proveedores (CECP)
export const conciliacionProveedoresApi = {
  list: (params = {}) => api.get('/conciliacion/', { params }),
  get: (id) => api.get(`/conciliacion/${id}`),  upload: (file, { proveedor_id, notas } = {}, onProgress) => {
    const formData = new FormData();
    formData.append('file', file);
    if (proveedor_id) formData.append('proveedor_id', proveedor_id);
    if (notas) formData.append('notas', notas);
    return api.post('/conciliacion/upload', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
      timeout: 240000,
      onUploadProgress: onProgress,
    });
  },
  aprobar: (id, accion, nota = '') => api.post(`/conciliacion/${id}/aprobar`, { accion, nota }),
  rematch: (id) => api.post(`/conciliacion/${id}/rematch`, {}, { timeout: 60000 }),
  editarFacturaRaw: (id, data) => api.patch(`/conciliacion/${id}/factura-raw`, data, { timeout: 60000 }),
  getConfig: () => api.get('/conciliacion/config'),
  setConfig: (data) => api.put('/conciliacion/config', data),
  // Informe consolidado de pendientes (faltantes / dif. monto / en RCV s/contab.)
  getInformePendientes: () => api.get('/conciliacion/informe-pendientes'),
  getInformeConfig: () => api.get('/conciliacion/informe-pendientes/config'),
  setInformeConfig: (data) => api.put('/conciliacion/informe-pendientes/config', data),
  enviarInformeAhora: () => api.post('/conciliacion/informe-pendientes/enviar-correo', {}, { timeout: 60000 }),
  listBloqueados: () => api.get('/conciliacion/proveedores/bloqueados'),
  desbloquearProveedor: (proveedor_id, motivo, tipos = null) =>
    api.post('/conciliacion/desbloquear-proveedor', { proveedor_id, motivo, tipos }),
  // Email auto-ingest (casilla concilia@) — run-now corre en segundo plano; polling vía status
  emailIngestStatus: () => api.get('/conciliacion/email-ingest/status'),
  emailIngestRunNow: () => api.post('/conciliacion/email-ingest/run-now'),
  // Avisos diarios (bloqueo progresivo + email)
  avisosStatus: () => api.get('/conciliacion/avisos-bloqueo/status'),
  avisosRunNow: (force = false) => api.post('/conciliacion/avisos-bloqueo/run-now', {}, { params: { force } }),
  estadoProveedor: ({ proveedor_id, rut } = {}) =>
    api.get('/conciliacion/estado-proveedor', { params: { proveedor_id, rut } }),
};

export const segurosApi = {
  uf: (fecha) => api.get('/seguros/uf', { params: fecha ? { fecha } : {} }),
  listPolizas: (params = {}) => api.get('/seguros/polizas', { params }),
  detallePoliza: (id) => api.get(`/seguros/polizas/${id}`),
  crearPoliza: (data) => api.post('/seguros/polizas', data),
  editarPoliza: (id, data) => api.put(`/seguros/polizas/${id}`, data),
  anularPoliza: (id) => api.delete(`/seguros/polizas/${id}`),
  pagarCuota: (id, numero, data = {}) => api.post(`/seguros/polizas/${id}/cuotas/${numero}/pagar`, data),
  despagarCuota: (id, numero) => api.post(`/seguros/polizas/${id}/cuotas/${numero}/despagar`),
  calendario: (year, month) => api.get('/seguros/calendario', { params: { year, month } }),
  importarExcel: (formData) => api.post('/seguros/importar-excel', formData, { headers: { 'Content-Type': 'multipart/form-data' }, timeout: 120000 }),
  syncFlujoCaja: () => api.post('/seguros/sync-flujo-caja'),
  getRecordatorioConfig: () => api.get('/seguros/recordatorio/config'),
  putRecordatorioConfig: (data) => api.put('/seguros/recordatorio/config', data),
  previewRecordatorio: () => api.get('/seguros/recordatorio/preview'),
  sendRecordatorioNow: () => api.post('/seguros/recordatorio/send-now', {}, { timeout: 60000 }),
  matriz: () => api.get('/seguros/matriz', { timeout: 90000 }),
  pagarYEgreso: (data) => api.post('/seguros/pagar-y-egreso', data, { timeout: 90000 }),
  estadoCuotas: (cuotas) => api.post('/seguros/cuotas/estado', { cuotas }),
  // Iter 368 — cuenta contable de los egresos + auditoría/recategorización en Alegra
  getConfig: () => api.get('/seguros/config', { timeout: 60000 }),
  setConfig: (data) => api.put('/seguros/config', data, { timeout: 60000 }),
  listarEgresos: () => api.get('/seguros/egresos', { timeout: 90000 }),
  recategorizarEgresos: (data) => api.post('/seguros/egresos/recategorizar', data, { timeout: 90000 }),
  proveedoresParaAseguradora: (aseguradora) =>
    api.get(`/seguros/aseguradoras/${encodeURIComponent(aseguradora)}/proveedores-match`),
  actualizarRutAseguradora: (data) => api.put('/seguros/aseguradora/rut', data),
};

// Lotes VB (Firma Visto Bueno por Lote)
export const lotesVBApi = {
  listar: (estado) => api.get('/compras/lotes-vb', { params: estado ? { estado } : {} }),
  detalle: (loteId) => api.get(`/compras/lotes-vb/${loteId}`),
  crear: (data) => api.post('/compras/lotes-vb', data),
  eliminar: (loteId) => api.delete(`/compras/lotes-vb/${loteId}`),
  agregarFacturas: (loteId, facturas) => api.post(`/compras/lotes-vb/${loteId}/facturas`, facturas),
  eliminarFactura: (loteId, facturaId) => api.delete(`/compras/lotes-vb/${loteId}/facturas/${facturaId}`),
  darVB: (loteId, acciones) => api.put(`/compras/lotes-vb/${loteId}/vb`, { acciones }),
  enviarContabilizar: (loteId) => api.post(`/compras/lotes-vb/${loteId}/enviar-contabilizar`),
  buscarFacturas: (q, loteIdExcluir = null) => api.get('/compras/lotes-vb/buscar-facturas', {
    params: loteIdExcluir ? { q, lote_id_excluir: loteIdExcluir } : { q }
  }),
  lookupRecepcion: (numRec) => api.get(`/compras/lotes-vb/lookup-recepcion/${numRec}`),
};

// Notas de Crédito de compra
export const notasCreditoApi = {
  list: (params = {}) => api.get('/contabilidad/notas-credito', { params }),
  sugerencias: (folio) => api.get(`/contabilidad/notas-credito/${folio}/sugerencias`),
  linkear: (folio, factura_folio) =>
    api.post(`/contabilidad/notas-credito/${folio}/linkear`, { factura_folio }),
  quitarLink: (folio) => api.delete(`/contabilidad/notas-credito/${folio}/linkear`),
  marcarAplicada: (folio, motivo, alegra_credit_note_id = null) =>
    api.post(`/contabilidad/notas-credito/${folio}/marcar-aplicada`, {
      motivo,
      alegra_credit_note_id,
    }),
  revertirAplicacion: (folio) =>
    api.post(`/contabilidad/notas-credito/${folio}/revertir-aplicacion`),
  autoLinkearBatch: () => api.post('/contabilidad/notas-credito/auto-linkear-batch'),
  aplicarAlegra: (folio, { motivo = '', dry_run = true, numero_debit_note = null } = {}) =>
    api.post(`/contabilidad/notas-credito/${folio}/aplicar-alegra`, {
      motivo,
      dry_run,
      numero_debit_note,
    }),
};

// Notas de Crédito de venta (emisor CHIRINO) — Iter 236.s Feb 2026
export const notasCreditoVentaApi = {
  list: (params = {}) => api.get('/contabilidad/notas-credito-venta', { params }),
  sugerencias: (folio) =>
    api.get(`/contabilidad/notas-credito-venta/${folio}/sugerencias`),
  linkear: (folio, factura_folio) =>
    api.post(`/contabilidad/notas-credito-venta/${folio}/linkear`, {
      factura_folio,
    }),
  quitarLink: (folio) =>
    api.delete(`/contabilidad/notas-credito-venta/${folio}/linkear`),
  aplicar: (folio, { motivo = '', dry_run = true } = {}) =>
    api.post(`/contabilidad/notas-credito-venta/${folio}/aplicar`, {
      motivo,
      dry_run,
    }),
  autoLinkearBatch: () =>
    api.post('/contabilidad/notas-credito-venta/auto-linkear-batch'),
};

// TEF Banco de Chile (config, bancos, cuentas de proveedores) — Admin/Gerencia
export const tefApi = {
  getConfig: () => api.get('/tef/config'),
  putConfig: (data) => api.put('/tef/config', data),
  listBancos: () => api.get('/tef/bancos'),
  crearBanco: (data) => api.post('/tef/bancos', data),
  actualizarBanco: (cmf, data) => api.put(`/tef/bancos/${encodeURIComponent(cmf)}`, data),
  eliminarBanco: (cmf) => api.delete(`/tef/bancos/${encodeURIComponent(cmf)}`),
  listCuentas: (params = {}) => api.get('/tef/cuentas', { params }),
  crearCuenta: (data) => api.post('/tef/cuentas', data),
  actualizarCuenta: (id, data) => api.put(`/tef/cuentas/${id}`, data),
  eliminarCuenta: (id) => api.delete(`/tef/cuentas/${id}`),
  // Iter 292.m — bootstrap de cuentas en el banco (CPN.TXT, monto $1)
  generarArchivoCuentasNuevas: () => api.post('/tef/cuentas/generar-archivo-nuevas'),
};

// Iter 373 — Auditoría nocturna de RUT y razón social de proveedores vs DTE/RCV
export const auditoriaProveedoresApi = {
  list: (params = {}) => api.get('/auditoria-proveedores/nombres', { params }),
  conteo: () => api.get('/auditoria-proveedores/nombres/conteo'),
  ejecutar: () => api.post('/auditoria-proveedores/nombres/ejecutar', {}, { timeout: 180000 }),
  corregir: (id, nombre) => api.post(`/auditoria-proveedores/nombres/${id}/corregir`, nombre ? { nombre } : {}),
  ignorar: (id, motivo) => api.post(`/auditoria-proveedores/nombres/${id}/ignorar`, { motivo }),
  reabrir: (id) => api.post(`/auditoria-proveedores/nombres/${id}/reabrir`),
  corregirRutTodos: () => api.post('/auditoria-proveedores/nombres/corregir-rut-todos', {}, { timeout: 300000 }),
};

export default api;
